import { ChangeEvent, useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import IntlTelInput from 'intl-tel-input/reactWithUtils';
import 'intl-tel-input/styles';
import { useLocation, useNavigate } from 'react-router-dom';

import { UniversignValidationCoordonneesRequestSource } from '@/api/placementApi.schemas';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import FormTextError from '@/components/formTextError/formTextError';
import FormTextSuccess from '@/components/formTextSuccess/formTextSuccess';
import InputFile from '@/components/inputFile/inputFile';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import TextInput from '@/components/TextInput/TextInput';
import { NFD_DIACRITICS } from '@/constants/regex';
import { ROUTE } from '@/constants/route';
import { useAuth } from '@/hooks/useAuth';
import { useFaireVersementJustificatifsCodeEmail } from '@/hooks/useFaireVersementJustificatifsCodeEmail';
import { useFaireVersementJustificatifsCoordonnees } from '@/hooks/useFaireVersementJustificatifsCoordonnees';
import { useFaireVersementJustificatifsSauvegarder } from '@/hooks/useFaireVersementJustificatifsSauvegarder';
import { useFaireVersementJustificatifsValidationUniversign } from '@/hooks/useFaireVersementJustificatifsValidationUniversign';
import { useRappelConseiller } from '@/hooks/useRappelConseiller';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { ModalRappel } from '@/modals/ContentModalRappel/ContentModalRappel';
import {
  modalCniContent,
  modalPasseportContent,
  modalTelephoneContent,
} from '@/modals/ContentModalVersementJustificatifs/ContentModalVersementJustificatifs';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';
import base64ToBlob from '@/utils/functions/base64ToBlob/base64ToBlob';
import { log } from '@/utils/functions/logger/logger';

import styles from './styles.module.css';

interface VersementJustificatifsFilesType {
  idSouscription: number;
  typeDocumentIdentite: string;
  justificatifIdentiteRecto: string;
  justificatifIdentiteVerso: string;
  justificatifTelephone: string;
  justificatifOrigineFonds: string;
}

interface RouteParams {
  ':familleContrat': string;
  ':typeContrat': string;
}

export const FaireVersementJustificatifs: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { windowWidth } = useScreenSize();
  const { state } = useLocation();

  const versementData = JSON.parse(sessionStorage.parcoursVersementData);

  const tailleMaxMo = 10;

  const [showLoader, setShowLoader] = useState(false);

  const FaireVersementJustificatifsCoordonnees = useFaireVersementJustificatifsCoordonnees();
  const FaireVersementJustificatifsCodeEmail = useFaireVersementJustificatifsCodeEmail();
  const FaireVersementJustificatifsSauvegarder = useFaireVersementJustificatifsSauvegarder();
  const FaireVersementJustificatifsValidationUniversign = useFaireVersementJustificatifsValidationUniversign();

  const autresOrigines = ['gains-aux-jeux', 'heritage-donation', 'cession-actif', 'autre-a-preciser'];
  const formatsValides = ['PDF', 'JPG', 'JPEG', 'PNG', 'TIFF'];

  let initialEmailTelUniversign = 'y**@g**.**';
  if (state?.universignErrorVersement?.emailUtilise) {
    initialEmailTelUniversign = state?.universignErrorVersement.emailUtilise;
  } else if (state?.universignErrorVersement?.telephoneUtilise) {
    initialEmailTelUniversign = state?.universignErrorVersement.telephoneUtilise;
  }
  const [emailTelUniversign, setEmailTelUniversign] = useState(initialEmailTelUniversign);

  const [universignCoordonnees, setUniversignCoordonnees] = useState({
    idSouscription: state?.idSouscription,
    email: '',
    telephone: '',
    code: '',
  });
  const [birthdate, setBirthdate] = useState('');

  const [origineFondsLabel, setOrigineFondsLabel] = useState('');
  const [origineFondsInfo, setOrigineFondsInfo] = useState('');

  const [showModalTelephone, setShowModalTelephone] = useState(false);
  const [showModalBonnesPratiquesCni, setShowModalBonnesPratiquesCni] = useState(false);
  const [showModalBonnesPratiquesPasseport, setShowModalBonnesPratiquesPasseport] = useState(false);
  const [showModalCodeEmail, setShowModalCodeEmail] = useState(false);
  const [showModalRappel, setShowModalRappel] = useState(false);

  const [showTextCodeEmailResend, setShowTextCodeEmailResend] = useState(false);

  const [submitButtonLabel, setSubmitButtonLabel] = useState(
    state?.universignErrorVersement?.emailUtilise ? 'Valider' : 'Aller à la signature'
  );
  const [showContactConseillerButton, setShowContactConseillerButton] = useState(
    state?.universignErrorVersement?.coordonneesUtilise === true &&
      state?.universignErrorVersement?.emailUtilise === null &&
      state?.universignErrorVersement?.telephoneUtilise === null
  );

  const [missingValue, setMissingValue] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const [errorJustificatif, setErrorJustificatif] = useState(state?.universignErrorVersement?.success === false);
  const [displayErrorJustificatif, setDisplayErrorJustificatif] = useState(false);

  const [erreurCompression, setErreurCompression] = useState({
    'justificatif-identite-1-recto': false,
    'justificatif-identite-1-verso': false,
    'justificatif-telephone': false,
    'justificatif-origine_fonds': false,
  });

  const [isFormValid, setIsFormValid] = useState({
    justificatifIdentiteRecto: true,
    justificatifIdentiteVerso: true,
    justificatifTelephone: true,
    justificatifOrigineFonds: true,
  });

  const [versementJustificatifError, setVersementJustificatifError] = useState({
    pieceLu: state?.universignErrorVersement?.pieceLu === false,
    infosNonCorrespondante: state?.universignErrorVersement?.infosNonCorrespondante === true,
    pieceExpire: state?.universignErrorVersement?.pieceExpire === true,
    coordonneesUtilisees: {
      mail: !!(
        state?.universignErrorVersement?.coordonneesUtilise === true &&
        (state?.universignErrorVersement?.emailUtilise !== null ||
          (state?.universignErrorVersement?.emailUtilise === null &&
            state?.universignErrorVersement?.telephoneUtilise === null))
      ),
      phone: !!(
        state?.universignErrorVersement?.coordonneesUtilise === true &&
        (state?.universignErrorVersement?.telephoneUtilise !== null ||
          (state?.universignErrorVersement?.emailUtilise === null &&
            state?.universignErrorVersement?.telephoneUtilise === null))
      ),
    },
  });

  const [universignTelMailError, setUniversignTelMailError] = useState(false);
  const [universignTelMailErrorText, setUniversignTelMailErrorText] = useState('');
  const [universignCodeError, setUniversignCodeError] = useState(false);
  const [universignCodeErrorText, setUniversignCodeErrorText] = useState('');

  const options = [
    { value: 'passeport', label: 'Passeport' },
    { value: 'cni', label: "Carte d'identité" },
    { value: 'carte-de-sejour', label: 'Carte de sejour' },
  ];

  const previousBreadcrumb: BreadcrumbItem = state?.breadcrumbItems?.at(-1) ?? {
    label: 'Mes opérations',
    path: ROUTE.OPERATIONS,
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    previousBreadcrumb,
    { label: 'Mes justificatifs', path: ROUTE.FAIREVERSEMENTJUSTIFICATIFS },
  ];

  const mapRoute: RouteParams = {
    ':familleContrat':
      versementData.contratFamille.toLowerCase() === 'assurancevie'
        ? 'assurance-vie'
        : versementData.contratFamille.toLowerCase(),
    ':typeContrat': versementData.contratNomProduit
      .toLowerCase()
      .replaceAll(' ', '-')
      .normalize('NFD')
      .replaceAll(NFD_DIACRITICS, ''),
  };

  let showOrigineFondsInput = true;
  if (
    versementData.montantVersement < 150000 &&
    !autresOrigines.includes(versementData.origineCapitaux) &&
    sessionStorage.getItem(versementData['justificatif-origine_fonds'].name) !== ''
  ) {
    sessionStorage.setItem(
      'parcoursVersementData',
      JSON.stringify({
        ...versementData,
        'justificatif-origine_fonds': {
          ...versementData['justificatif-origine_fonds'],
          name: '',
          size: 0,
          symbol: '',
          extension: '',
          url: '',
        },
      })
    );
    showOrigineFondsInput = false;
  }

  const [justificatifsUrl, setJustificatifsUrl] = useState({
    justificatifIdentiteRectoURL: versementData['justificatif-identite-1-recto'].url,
    justificatifIdentiteversoURL: versementData['justificatif-identite-1-verso'].url,
    justificatifTelephoneURL: versementData['justificatif-telephone'].url,
    justificatifOrigineFondsURL: versementData['justificatif-origine_fonds'].url,
  });

  let initialTypeDocumentIdentite = (versementData.typeIdentite ?? 'passeport').toLowerCase();

  if (state?.data.validFile['justificatif-identite-1'] === true && state?.universignErrorVersement?.success !== false) {
    initialTypeDocumentIdentite = 'passeport';
  } else if (!versementData.typeIdentite || versementData.typeIdentite === 'passeport') {
    initialTypeDocumentIdentite = 'passeport';
  }

  const [versementJustificatifsData, setVersementJustificatifsData] = useState<VersementJustificatifsFilesType>({
    idSouscription: state?.idSouscription ?? versementData.idSouscription,
    typeDocumentIdentite: initialTypeDocumentIdentite,
    justificatifIdentiteRecto: versementData['justificatif-identite-1-recto'].name,
    justificatifIdentiteVerso: versementData['justificatif-identite-1-verso'].name,
    justificatifTelephone: versementData['justificatif-telephone'].name,
    justificatifOrigineFonds: versementData['justificatif-origine_fonds'].name,
  });

  const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);

  const rappel = useRappelConseiller();
  const openModalRappel = () => {
    rappel.mutate();
    openModal('showModalRappel');
  };

  let errorMessageIdentiteRecto = '';
  if (displayErrorJustificatif) {
    errorMessageIdentiteRecto = 'Le fichier a été refusé par notre équipe';
  } else if (erreurCompression['justificatif-identite-1-recto']) {
    errorMessageIdentiteRecto = 'La taille du fichier dépasse les 4 Mo autorisés';
  }

  let errorMessageIdentiteVerso = '';
  if (displayErrorJustificatif) {
    errorMessageIdentiteVerso = 'Le fichier a été refusé par notre équipe';
  } else if (erreurCompression['justificatif-identite-1-verso']) {
    errorMessageIdentiteVerso = 'La taille du fichier dépasse les 4 Mo autorisés';
  }

  const handleSendVerificationCoordonnees = (source: string) => {
    const coordonneesPayload = {
      ...universignCoordonnees,
      source: UniversignValidationCoordonneesRequestSource.versement,
    };
    FaireVersementJustificatifsCoordonnees.mutate(coordonneesPayload, {
      onSuccess: (data) => {
        if (data.valide) {
          if (universignCoordonnees.email !== '') {
            TRACKING_INSTANCE.setTcVars({
              page_nom: 'verification email',
              page_template: 'espace client',
              user_id: user?.id,
              user_client: user?.type === 'Client',
              user_connexion: user?.success,
              user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
              tunnel_produit: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratNomProduit,
              tunnel_metier: 'espace client placement',
              tunnel_sous_metier: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratFamille,
              tunnel_categorie_produit: 'parcours de versement',
            });
            void TRACKING_INSTANCE.triggerEvent('page', undefined, {
              event_id: 'page',
              event_categorie: 'affichage espace client',
              event_nom: 'verification email',
              event_page: 'verification email',
            });
            openModal('showModalCodeEmail');

            if (source === 'renvoyer') {
              setUniversignCodeError(false);
              setShowTextCodeEmailResend(true);
            }
            setShowLoader(false);
          } else if (universignCoordonnees.telephone !== '') {
            sendToUniverSign();
          }
        } else {
          setShowLoader(false);

          if (universignCoordonnees.email !== '') {
            setUniversignTelMailError(true);
            setUniversignTelMailErrorText("L'email ne correspond pas");
          } else if (universignCoordonnees.telephone !== '') {
            setUniversignTelMailError(true);
            setUniversignTelMailErrorText('Le numéro de téléphone ne correspond pas');
          }
        }
      },
      onError: (error) => {
        log.error(`FaireVersementJustificatifsCoordonnees mutation failed: ${error}`);
        navigate(ROUTE.ERREUR);
      },
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmitCodeVerification = (e: React.FormEvent) => {
    e.preventDefault();
    setUniversignCodeError(false);
    setShowTextCodeEmailResend(false);

    if (universignCoordonnees.code === '') {
      setUniversignCodeError(true);
      setUniversignCodeErrorText('Champs requis');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setShowLoader(true);

    FaireVersementJustificatifsCodeEmail.mutate(universignCoordonnees, {
      onSuccess: (data) => {
        if (data.valide) {
          setUniversignCodeError(false);
          sendToUniverSign();
        } else {
          setUniversignCodeError(true);
          setUniversignCodeErrorText('Code invalide');
        }
      },
      onError: (error) => {
        log.error(`FaireVersementJustificatifsCodeEmail mutation failed: ${error}`);
        setUniversignCodeError(true);
        setUniversignCodeErrorText("Erreur lors de l'envoi du code");
      },
    });
    setShowLoader(false);
  };

  const modalCodeEmailContent = (
    <>
      {showTextCodeEmailResend && (
        <div className={styles.resendEmailConfirmation}>
          <FormTextSuccess text="Le code de vérification vient d’être renvoyé" />
        </div>
      )}
      <p>
        Veuillez saisir le code de vérification reçu à l&apos;adresse&nbsp;:{' '}
        <span className={`bolder-text ${styles.universignErrorEmail}`}>{universignCoordonnees.email}</span>
      </p>

      <form
        onSubmit={(e) => {
          handleSubmitCodeVerification(e);
        }}
      >
        <TextInput
          label="Code de vérification"
          labelFor="universign-email-code"
          placeholder="123456"
          type="text"
          isError={universignCodeError}
          textError={universignCodeErrorText}
          onChange={(e) => {
            setUniversignCoordonnees({ ...universignCoordonnees, code: e.currentTarget.value });
            setUniversignCodeError(false);
          }}
        />

        <input className="hide-element" type="submit" />
      </form>

      <div
        className={styles.resendCode}
        role="button"
        tabIndex={0}
        onClick={() => {
          setShowLoader(true);
          handleSendVerificationCoordonnees('renvoyer');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowLoader(true);
            handleSendVerificationCoordonnees('renvoyer');
          }
        }}
      >
        Renvoyer le code
      </div>
    </>
  );

  const OnChangePhoneInputError = (value: string) => {
    if (value !== universignCoordonnees.telephone)
      setUniversignCoordonnees({ ...universignCoordonnees, telephone: value });
  };

  const setJustificatifsUrlFunction = (whichFile: string, blobUrl: string) => {
    switch (whichFile) {
      case 'justificatif-identite-1-recto':
        setJustificatifsUrl({ ...justificatifsUrl, justificatifIdentiteRectoURL: blobUrl });
        break;

      case 'justificatif-identite-1-verso':
        setJustificatifsUrl({ ...justificatifsUrl, justificatifIdentiteversoURL: blobUrl });
        break;

      case 'justificatif-telephone':
        setJustificatifsUrl({ ...justificatifsUrl, justificatifTelephoneURL: blobUrl });
        break;

      case 'justificatif-origine_fonds':
        setJustificatifsUrl({ ...justificatifsUrl, justificatifOrigineFondsURL: blobUrl });
        break;
    }
  };

  const sendFile = (base64: string, whichFile: string, file: File) => {
    const dataToSend: VersementJustificatifsFilesType = {
      idSouscription: versementJustificatifsData.idSouscription,
      typeDocumentIdentite: '',
      justificatifIdentiteRecto: '',
      justificatifIdentiteVerso: '',
      justificatifTelephone: '',
      justificatifOrigineFonds: '',
    };

    switch (whichFile) {
      case 'justificatif-identite-1-recto':
        dataToSend.typeDocumentIdentite = versementJustificatifsData.typeDocumentIdentite;
        dataToSend.justificatifIdentiteRecto = base64;
        break;

      case 'justificatif-identite-1-verso':
        dataToSend.typeDocumentIdentite = versementJustificatifsData.typeDocumentIdentite;
        dataToSend.justificatifIdentiteVerso = base64;
        break;

      case 'justificatif-telephone':
        dataToSend.justificatifTelephone = base64;
        break;

      case 'justificatif-origine_fonds':
        dataToSend.justificatifOrigineFonds = base64;
        break;
    }

    FaireVersementJustificatifsSauvegarder.mutate(dataToSend, {
      onSuccess: (data) => {
        setDisableSubmitBtn(false);

        const extensionTmp = file.name.split('.').pop() || '';

        const blobUrl = URL.createObjectURL(base64ToBlob(data.preview, 'image/jpeg'));

        setJustificatifsUrlFunction(whichFile, blobUrl);

        let tempSize = (file.size / 1000000).toFixed(2);
        if (file.size < 1000) {
          tempSize = file.size.toFixed(2);
        } else if (file.size < 1000000) {
          tempSize = (file.size / 1000).toFixed(2);
        }

        let tempSymbol = '';
        if (file.size < 1000) {
          tempSymbol = 'o';
        } else if (file.size < 1000000) {
          tempSymbol = 'Ko';
        } else {
          tempSymbol = 'Mo';
        }

        sessionStorage.setItem(
          'parcoursVersementData',
          JSON.stringify({
            ...versementData,
            [whichFile]: {
              ...versementData[whichFile],
              name: file.name,
              size: tempSize,
              symbol: tempSymbol,
              extension: extensionTmp?.toUpperCase(),
              url: blobUrl,
            },
          })
        );
      },
      onError: (error: Error) => {
        if (error.message === 'Fichier trop volumineux') {
          setErreurCompression({
            ...erreurCompression,
            [whichFile]: true,
          });
        }

        setDisableSubmitBtn(false);
        log.error(`Erreur lors de l'envoi du fichier ${whichFile} : ${error.message}`);
      },
    });
  };

  const getBase64 = (file: File) => {
    return new Promise((resolve) => {
      let baseURL: string | ArrayBuffer | null = '';

      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        baseURL = reader.result;
        resolve(baseURL);
      };
    });
  };

  const convertToBase64 = (whichFile: string, file: File) => {
    getBase64(file)
      .then((result: unknown) => {
        if (typeof result !== 'string') {
          log.error(`Le résultat de la conversion base64 n'est pas une string pour ${whichFile}`);
          return;
        }

        const result64 = result.split('base64,')[1];
        const extensionTmp = file.name.split('.').pop() || '';

        switch (whichFile) {
          case 'justificatif-identite-1-recto':
            setVersementJustificatifsData({ ...versementJustificatifsData, justificatifIdentiteRecto: result64 });
            break;

          case 'justificatif-identite-1-verso':
            setVersementJustificatifsData({ ...versementJustificatifsData, justificatifIdentiteVerso: result64 });
            break;

          case 'justificatif-telephone':
            setVersementJustificatifsData({ ...versementJustificatifsData, justificatifTelephone: result64 });
            break;

          case 'justificatif-origine_fonds':
            setVersementJustificatifsData({ ...versementJustificatifsData, justificatifOrigineFonds: result64 });
            break;
        }

        if (file.size < tailleMaxMo * 1000000 && formatsValides.includes(extensionTmp.toUpperCase())) {
          if (result64 !== '') {
            sendFile(result64, whichFile, file);
          }
        } else {
          setJustificatifsUrlFunction(whichFile, URL.createObjectURL(file));
          setDisableSubmitBtn(false);
        }
      })
      .catch((err) => {
        log.error(`Erreur lors de la conversion du fichier ${whichFile} en base64 : ${err}`);
      });
  };

  const setFile = (whichFile: string, file: File) => {
    setDisplayErrorJustificatif(false);
    setMissingValue(false);

    setErreurCompression({
      ...erreurCompression,
      [whichFile]: false,
    });

    if (file.size !== 0) setDisableSubmitBtn(true);

    switch (whichFile) {
      case 'justificatif-identite-1-recto':
        convertToBase64(whichFile, file);
        break;

      case 'justificatif-identite-1-verso':
        convertToBase64(whichFile, file);
        break;

      case 'justificatif-telephone':
        convertToBase64(whichFile, file);
        break;

      case 'justificatif-origine_fonds':
        convertToBase64(whichFile, file);
        break;
    }
  };

  const handleFileValidity = (whichFile: string, isValid: boolean) => {
    switch (whichFile) {
      case 'justificatif-identite-1-recto':
        setIsFormValid({ ...isFormValid, justificatifIdentiteRecto: isValid });
        break;

      case 'justificatif-identite-1-verso':
        setIsFormValid({ ...isFormValid, justificatifIdentiteVerso: isValid });
        break;

      case 'justificatif-telephone':
        setIsFormValid({ ...isFormValid, justificatifTelephone: isValid });
        break;

      case 'justificatif-origine_fonds':
        setIsFormValid({ ...isFormValid, justificatifOrigineFonds: isValid });
        break;
    }
  };

  const handleErrorBreadcrumbs = () => {
    if (
      (state?.universignErrorVersement?.emailUtilise && state?.universignErrorVersement?.telephoneUtilise === null) ||
      (state?.universignErrorVersement?.emailUtilise && state?.universignErrorVersement?.telephoneUtilise)
    ) {
      globalThis.history.go(-1);
    }
    setUniversignTelMailError(false);
    setUniversignCoordonnees({ ...universignCoordonnees, email: '' });
    setUniversignCoordonnees({ ...universignCoordonnees, telephone: '' });
    setUniversignCoordonnees({ ...universignCoordonnees, code: '' });
    setMissingValue(false);

    setShowContactConseillerButton(false);
    setSubmitButtonLabel('Aller à la signature');

    setErrorJustificatif(false);

    setIsFormValid({
      ...isFormValid,
      justificatifTelephone: true,
    });

    setVersementJustificatifError({
      pieceLu: false,
      infosNonCorrespondante: false,
      pieceExpire: false,
      coordonneesUtilisees: {
        mail: false,
        phone: false,
      },
    });
  };

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    switch (modal) {
      case 'showModalTelephone':
        setShowModalTelephone(true);
        break;
      case 'showModalBonnesPratiquesCni':
        setShowModalBonnesPratiquesCni(true);
        break;
      case 'showModalBonnesPratiquesPasseport':
        setShowModalBonnesPratiquesPasseport(true);
        break;
      case 'showModalCodeEmail':
        setShowModalCodeEmail(true);
        break;
      case 'showModalRappel':
        setShowModalRappel(true);
        break;

      default:
        break;
    }
  };

  const closeModal = () => {
    setShowModalTelephone(false);
    setShowModalBonnesPratiquesCni(false);
    setShowModalBonnesPratiquesPasseport(false);

    setShowModalCodeEmail(false);
    setUniversignCodeError(false);
    setShowTextCodeEmailResend(false);

    setShowModalRappel(false);
  };

  const openBonnesPratiquesModale = () => {
    switch (versementJustificatifsData.typeDocumentIdentite) {
      case 'passeport':
        openModal('showModalBonnesPratiquesPasseport');
        break;

      case 'cni':
      default:
        openModal('showModalBonnesPratiquesCni');
        break;
    }
  };

  const scrollToInputError = (anchorId: string) => {
    const anchor = document.getElementById(anchorId);
    anchor?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePhoneCall = () => {
    document.location.href = 'tel:+900300400';
  };

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setMissingValue(false);

    if (e.target.value === 'passeport') {
      sessionStorage.setItem(
        'parcoursVersementData',
        JSON.stringify({
          ...versementData,
          typeIdentite: e.target.value,
          'justificatif-identite-1-recto': {
            name: '',
            size: 0,
            symbol: '',
            extension: '',
            url: '',
          },
          'justificatif-identite-1-verso': {
            name: '',
            size: 0,
            symbol: '',
            extension: '',
            url: '',
          },
        })
      );

      setVersementJustificatifsData({
        ...versementJustificatifsData,
        typeDocumentIdentite: e.target.value,
        justificatifIdentiteRecto: '',
        justificatifIdentiteVerso: '',
      });
      setIsFormValid({ ...isFormValid, justificatifIdentiteVerso: true });
    } else if (versementJustificatifsData.typeDocumentIdentite === 'passeport') {
      sessionStorage.setItem(
        'parcoursVersementData',
        JSON.stringify({
          ...versementData,
          typeIdentite: e.target.value,
          'justificatif-identite-1-recto': {
            name: '',
            size: 0,
            symbol: '',
            extension: '',
            url: '',
          },
          'justificatif-identite-1-verso': {
            name: '',
            size: 0,
            symbol: '',
            extension: '',
            url: '',
          },
        })
      );
      setVersementJustificatifsData({
        ...versementJustificatifsData,
        typeDocumentIdentite: e.target.value,
        justificatifIdentiteRecto: '',
        justificatifIdentiteVerso: '',
      });
    } else {
      sessionStorage.setItem(
        'parcoursVersementData',
        JSON.stringify({
          ...versementData,
          typeIdentite: e.target.value,
        })
      );
      setVersementJustificatifsData({ ...versementJustificatifsData, typeDocumentIdentite: e.target.value });
    }
  };

  const checkFileForm = () => {
    if (
      versementJustificatifsData.typeDocumentIdentite === 'cni' ||
      versementJustificatifsData.typeDocumentIdentite === 'carte-de-sejour'
    ) {
      if (
        (state?.data.validFile['justificatif-identite-1-recto'] !== true ||
          versementJustificatifError.pieceLu ||
          versementJustificatifError.pieceExpire ||
          versementJustificatifError.infosNonCorrespondante) &&
        versementJustificatifsData.justificatifIdentiteRecto === ''
      ) {
        scrollToInputError('faire-versement-justif-anchor-pi');
        return true;
      } else if (
        (state?.data.validFile['justificatif-identite-1-verso'] !== true ||
          versementJustificatifError.pieceLu ||
          versementJustificatifError.pieceExpire ||
          versementJustificatifError.infosNonCorrespondante) &&
        versementJustificatifsData.justificatifIdentiteVerso === ''
      ) {
        scrollToInputError('faire-versement-justif-anchor-pi');
        return true;
      }
    } else if (
      versementJustificatifsData.typeDocumentIdentite === 'passeport' &&
      (state?.data.validFile['justificatif-identite-1-recto'] !== true ||
        state?.data.validFile['justificatif-identite-1'] === undefined ||
        versementJustificatifError.pieceLu ||
        versementJustificatifError.pieceExpire ||
        versementJustificatifError.infosNonCorrespondante) &&
      versementJustificatifsData.justificatifIdentiteRecto === ''
    ) {
      scrollToInputError('faire-versement-justif-anchor-pi');
      return true;
    }
    if (!state?.data.validFile['justificatif-telephone'] && versementJustificatifsData.justificatifTelephone === '') {
      scrollToInputError('faire-versement-justif-anchor-telephone');
      return true;
    }
    if (
      state?.data.validFile['justificatif-origine_fonds'] === false &&
      versementJustificatifsData.justificatifOrigineFonds === '' &&
      showOrigineFondsInput
    ) {
      scrollToInputError('faire-versement-justif-anchor-origine-fonds');
      return true;
    }

    return false;
  };

  const sendToUniverSign = () => {
    setShowLoader(true);

    FaireVersementJustificatifsValidationUniversign.mutate(versementJustificatifsData.idSouscription, {
      onSuccess: (data) => {
        datadogRum.addAction('versement_universign_valide', {
          success: data.success,
          emailUtilise: !!data.emailUtilise,
          telephoneUtilise: !!data.telephoneUtilise,
          coordonneesUtilise: data.coordonneesUtilise,
          pieceLu: data.pieceLu,
          infosNonCorrespondante: data.infosNonCorrespondante,
          pieceExpire: data.pieceExpire,
        });

        setShowLoader(false);

        if (data.success === true) {
          navigate(
            ROUTE.FAIREVERSEMENTSIGNATURE.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
              return mapRoute[matched as keyof RouteParams];
            }),
            {
              state: { breadcrumbItems, data },
            }
          );
        } else {
          setErrorJustificatif(true);

          if (data.pieceLu === false || data.pieceMRZ === true) {
            setDisplayErrorJustificatif(true);
            setVersementJustificatifError({
              pieceLu: true,
              infosNonCorrespondante: false,
              pieceExpire: false,
              coordonneesUtilisees: {
                mail: false,
                phone: false,
              },
            });
          } else if (data.infosNonCorrespondante === true) {
            setDisplayErrorJustificatif(true);
            setVersementJustificatifError({
              pieceLu: false,
              infosNonCorrespondante: true,
              pieceExpire: false,
              coordonneesUtilisees: {
                mail: false,
                phone: false,
              },
            });
            setBirthdate(data.birthdate);
          } else if (data.pieceExpire === true) {
            setDisplayErrorJustificatif(true);
            setVersementJustificatifError({
              pieceLu: false,
              infosNonCorrespondante: false,
              pieceExpire: true,
              coordonneesUtilisees: {
                mail: false,
                phone: false,
              },
            });
          } else if (data.coordonneesUtilise === true) {
            if (data.emailUtilise === null && data.telephoneUtilise === null) {
              setVersementJustificatifError({
                pieceLu: false,
                infosNonCorrespondante: false,
                pieceExpire: false,
                coordonneesUtilisees: {
                  mail: true,
                  phone: true,
                },
              });
              setShowContactConseillerButton(true);
            } else if (data.emailUtilise !== null) {
              TRACKING_INSTANCE.setTcVars({
                page_nom: 'erreur signature',
                page_template: 'espace client',
                user_id: user?.id,
                user_client: user?.type === 'Client',
                user_connexion: user?.success,
                user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
                tunnel_produit: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratNomProduit,
                tunnel_metier: 'espace client placement',
                tunnel_sous_metier: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratFamille,
                tunnel_categorie_produit: 'parcours de versement',
              });
              void TRACKING_INSTANCE.triggerEvent('page', undefined, {
                event_id: 'page',
                event_categorie: 'affichage espace client',
                event_nom: 'erreur signature',
                event_page: 'erreur signature',
              });

              setEmailTelUniversign(data.emailUtilise);
              setVersementJustificatifError({
                pieceLu: false,
                infosNonCorrespondante: false,
                pieceExpire: false,
                coordonneesUtilisees: {
                  mail: true,
                  phone: false,
                },
              });
              setSubmitButtonLabel('Valider');
            } else if (data.telephoneUtilise !== null) {
              setEmailTelUniversign(data.telephoneUtilise);
              setVersementJustificatifError({
                pieceLu: false,
                infosNonCorrespondante: false,
                pieceExpire: false,
                coordonneesUtilisees: {
                  mail: false,
                  phone: true,
                },
              });
            }
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      onError: (error) => {
        navigate(ROUTE.ERREUR);
        log.error(`FaireVersementJustificatifsValidationUniversign mutation failed: ${error}`);
      },
    });
  };

  const handleClick = (e: React.FormEvent) => {
    e.preventDefault();
    setUniversignTelMailError(false);

    if (versementJustificatifError.coordonneesUtilisees.mail) {
      if (universignCoordonnees.email === '') {
        setUniversignTelMailError(true);
        setUniversignTelMailErrorText('Champs requis');
      } else {
        setShowLoader(true);

        handleSendVerificationCoordonnees('');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    } else if (versementJustificatifError.coordonneesUtilisees.phone) {
      if (universignCoordonnees.telephone === '' || versementJustificatifsData.justificatifTelephone === '') {
        if (universignCoordonnees.telephone === '') {
          setUniversignTelMailError(true);
          setUniversignTelMailErrorText('Champs requis');
        } else if (isPhoneValid === false) {
          setUniversignTelMailError(true);
          setUniversignTelMailErrorText('Veuillez rentrer un numéro valide');
        }
        if (versementJustificatifsData.justificatifTelephone === '') setMissingValue(true);

        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        handleSendVerificationCoordonnees('');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setMissingValue(true);

    if (
      checkFileForm() ||
      !Object.values(isFormValid).every((item) => item === true) ||
      !Object.values(erreurCompression).every((item) => item === false)
    ) {
      return;
    }

    setMissingValue(false);

    sendToUniverSign();
  };

  const displayLabel = () => {
    let currLabel = '';

    options.map((option) => {
      if (option.value === versementJustificatifsData.typeDocumentIdentite) currLabel = option.label;
    });
    return currLabel;
  };

  let pieceIdentiteInput = null;
  if (
    (state?.data.validFile['justificatif-identite-1'] === undefined && !errorJustificatif) ||
    (errorJustificatif && versementJustificatifError.infosNonCorrespondante) ||
    (errorJustificatif && versementJustificatifError.pieceLu) ||
    (errorJustificatif && versementJustificatifError.pieceExpire)
  ) {
    if (versementJustificatifsData.typeDocumentIdentite === 'passeport') {
      pieceIdentiteInput = (
        <MtxGrid className={styles.documentInput} row>
          <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
            <InputFile
              formats={formatsValides}
              label="Passeport"
              maxSize={tailleMaxMo}
              displayedMaxSize={4}
              blobUrl={
                justificatifsUrl.justificatifIdentiteRectoURL === ''
                  ? versementData['justificatif-identite-1-recto'].url
                  : justificatifsUrl.justificatifIdentiteRectoURL
              }
              isError={displayErrorJustificatif || erreurCompression['justificatif-identite-1-recto']}
              errorMessageInvalid={errorMessageIdentiteRecto}
              whichFile="justificatif-identite-1-recto"
              disableInput={versementJustificatifsData.justificatifIdentiteRecto !== ''}
              setFile={setFile}
              handleFileValidity={handleFileValidity}
            />
            {missingValue && versementJustificatifsData.justificatifIdentiteRecto === '' && (
              <FormTextError text="Champs requis" />
            )}
          </MtxGrid>
        </MtxGrid>
      );
    } else {
      pieceIdentiteInput = (
        <>
          <MtxGrid className={styles.documentInput} row>
            <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
              <InputFile
                formats={formatsValides}
                label={displayLabel() + ' (recto)'}
                maxSize={tailleMaxMo}
                displayedMaxSize={4}
                blobUrl={justificatifsUrl.justificatifIdentiteRectoURL}
                isError={displayErrorJustificatif || erreurCompression['justificatif-identite-1-recto']}
                errorMessageInvalid={errorMessageIdentiteRecto}
                whichFile="justificatif-identite-1-recto"
                disableInput={versementJustificatifsData.justificatifIdentiteRecto !== ''}
                setFile={setFile}
                handleFileValidity={handleFileValidity}
              />
              {missingValue && versementJustificatifsData.justificatifIdentiteRecto === '' && (
                <FormTextError text="Champs requis" />
              )}
            </MtxGrid>
          </MtxGrid>
          <MtxGrid className={styles.documentInput} row>
            <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
              <InputFile
                formats={formatsValides}
                label={displayLabel() + ' (verso)'}
                maxSize={tailleMaxMo}
                displayedMaxSize={4}
                blobUrl={justificatifsUrl.justificatifIdentiteversoURL}
                isError={displayErrorJustificatif || erreurCompression['justificatif-identite-1-verso']}
                errorMessageInvalid={errorMessageIdentiteVerso}
                whichFile="justificatif-identite-1-verso"
                disableInput={versementJustificatifsData.justificatifIdentiteVerso !== ''}
                setFile={setFile}
                handleFileValidity={handleFileValidity}
              />
              {missingValue && versementJustificatifsData.justificatifIdentiteVerso === '' && (
                <FormTextError text="Champs requis" />
              )}
            </MtxGrid>
          </MtxGrid>
        </>
      );
    }
  }

  useEffect(() => {
    const origineFonds = JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').origineCapitaux || '';
    if (origineFonds) {
      switch (origineFonds) {
        case 'revenu':
          setOrigineFondsLabel('Justificatif de revenu');
          setOrigineFondsInfo('Les 6 derniers relevés bancaire ou votre bulletin de salaire.');
          break;

        case 'epargne':
          setOrigineFondsLabel("Justificatif d'épargne");
          setOrigineFondsInfo(
            'Le dernier relevé de compte faisant apparaître la sortie des fonds ou relevé de situation du compte avant clôture ou transfert de moins de 3 mois.'
          );
          break;

        case 'gains-aux-jeux':
          setOrigineFondsLabel('Justificatif de gains aux jeux');
          setOrigineFondsInfo(
            'L’attestation de gain délivrée par l’organisme des jeux, ou la copie du chèque / avis de virement reçu de l’organisme des jeux.'
          );
          break;

        case 'heritage-donation':
          setOrigineFondsLabel("Justificatif d'héritage/donation");
          setOrigineFondsInfo(
            'L’acte notarié de la déclaration de succession ou du formulaire Cerfa tamponné par l’administration fiscale et relevés du compte à débiter permettant d’attester de l’origine et de la traçabilité des fonds.'
          );
          break;

        case 'cession-actif':
          setOrigineFondsLabel("Justificatif de cession d'actif");
          setOrigineFondsInfo(
            'L’acte notarié, le protocole de cession ou l’attestation du notaire avec prix et relevés du compte à débiter permettant d’attester de l’origine et de la traçabilité des fonds.'
          );
          break;

        case 'autre-a-preciser':
          setOrigineFondsLabel('Justificatif (Autre)');
          setOrigineFondsInfo('Tout élément permettant de démontrer la provenance des fonds.');
          break;
      }
    }

    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (user?.id && user.type && user.success) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: 'mes justificatifs',
        page_template: 'espace client',
        user_id: user.id,
        user_client: user.type === 'Client',
        user_connexion: user.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
        tunnel_produit: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratNomProduit,
        tunnel_metier: 'espace client placement',
        tunnel_sous_metier: JSON.parse(sessionStorage.getItem('parcoursVersementData') || '').contratFamille,
        tunnel_categorie_produit: 'parcours de versement',
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: 'mes justificatifs',
        event_page: 'mes justificatifs',
      });
    }
  }, [user?.id, user?.type, user?.success]);

  return (
    <>
      {showModalRappel && (
        <ModaleDefaut
          content={<ModalRappel onClose={closeModal} data={rappel.data} />}
          onClickClose={closeModal}
          size="large"
          mobileStyle="defaut"
        />
      )}

      <MtxGrid className={styles.container}>
        {showLoader && <Loader />}

        {showModalCodeEmail && (
          <ModaleDefaut
            title="Vérifiez votre email de signature électronique"
            content={modalCodeEmailContent}
            btnLabel="Aller à la signature"
            btnVariant="primary"
            btnSize="large"
            onClickBtn={(e) => handleSubmitCodeVerification(e)}
            btnBottomLabel="Annuler"
            btnBottomVariant="tertiary"
            btnBottomSize="large"
            onClickBtnBottom={() => closeModal()}
            btnFullwidth={false}
            onClickClose={() => closeModal()}
            size="medium"
            mobileStyle="stick-to-bottom"
          />
        )}

        {showModalTelephone && (
          <ModaleDefaut
            title="Facture de téléphone mobile"
            content={modalTelephoneContent}
            btnLabel="J'ai compris"
            btnVariant="primary"
            btnSize="large"
            onClickBtn={() => closeModal()}
            btnFullwidth={false}
            onClickClose={() => closeModal()}
            mobileStyle="stick-to-bottom"
            size="large"
          />
        )}

        {showModalBonnesPratiquesCni && (
          <ModaleDefaut
            title="Quelles sont les bonnes pratiques&nbsp;?"
            content={modalCniContent}
            btnLabel="J'ai compris"
            btnVariant="primary"
            btnSize="large"
            onClickBtn={() => closeModal()}
            btnFullwidth={false}
            onClickClose={() => closeModal()}
            mobileStyle="defaut"
            size="large"
          />
        )}

        {showModalBonnesPratiquesPasseport && (
          <ModaleDefaut
            title="Quelles sont les bonnes pratiques&nbsp;?"
            content={modalPasseportContent}
            btnLabel="J'ai compris"
            btnVariant="primary"
            btnSize="large"
            onClickBtn={() => closeModal()}
            btnFullwidth={false}
            onClickClose={() => closeModal()}
            mobileStyle="defaut"
            size="large"
          />
        )}

        {versementJustificatifError.coordonneesUtilisees.mail === true ? (
          <>
            {windowWidth < 900 ? (
              <div
                className="parrainage-content-back-arrow"
                role="button"
                tabIndex={0}
                aria-label="Retour"
                onClick={handleErrorBreadcrumbs}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleErrorBreadcrumbs();
                  }
                }}
              >
                <i className="totem-icon totem-icon-arrow-left-big" />
              </div>
            ) : (
              <Breadcrumb items={breadcrumbItems} />
            )}

            {!errorJustificatif && <div className={styles.pageDivider}></div>}
          </>
        ) : (
          <MtxGrid className={styles.header} row>
            <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
              <Breadcrumb items={breadcrumbItems} />
              {state?.data.validFile['justificatif-identite-1'] === undefined && !errorJustificatif && (
                <div>
                  <h1>Mes justificatifs</h1>
                  <p className={styles.headerText}>
                    <span className="bolder-text">Le versement nécessite de vous créer une identité numérique</span>{' '}
                    afin de garantir la sécurité de la transaction
                  </p>
                  <p className={styles.headerText}>
                    <span className="bolder-text">Téléchargez une seule fois vos justificatifs</span> et passez
                    automatiquement cette étape lors des prochains versements
                  </p>
                </div>
              )}
            </MtxGrid>
          </MtxGrid>
        )}

        <div>
          {versementJustificatifError.pieceLu && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <div className={styles.universignError}>
                  <h3>
                    <i className="totem-icon totem-icon-cross-circle" />
                    Erreur avec la pièce d&apos;identité
                  </h3>

                  <p className={styles.universignErrorText}>
                    Nous n&apos;avons malheureusement pas réussi à lire votre justificatif d&apos;identité.
                  </p>

                  <p className={styles.universignErrorBold}>
                    Nous vous conseillons de re-télécharger votre justificatif d&apos;identité, en veillant à ce que le
                    document soit bien visible.
                  </p>

                  <div className={styles.infoBox}>
                    <i className="totem-icon totem-icon-info" />
                    <p className={styles.universignErrorText}>
                      <button className={styles.infoBoxUnderline} onClick={() => openBonnesPratiquesModale()}>
                        Accédez à nos conseils
                      </button>{' '}
                      pour faciliter la validation de votre justificatif d&apos;identité
                    </p>
                  </div>

                  <p className={styles.universignErrorText}>
                    Si le problème persiste ou que vos coordonnées ont changé, veuillez contacter notre Service Client
                    au{' '}
                    <span
                      className={styles.universignErrorPhone}
                      onClick={() => handlePhoneCall()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handlePhoneCall();
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label="Appeler le service client au 01 47 20 33 00"
                    >
                      01 47 20 33 00
                    </span>
                    .
                  </p>
                </div>
              </MtxGrid>
            </MtxGrid>
          )}

          {versementJustificatifError.infosNonCorrespondante && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <div className={styles.universignError}>
                  <h3>
                    <i className="totem-icon totem-icon-cross-circle" />
                    Erreur avec la pièce d&apos;identité
                  </h3>

                  <p className={styles.universignErrorText}>
                    Notre prestataire de signature électronique n&apos;a pas réussi à faire correspondre votre nom,
                    prénom et/ou date de naissance avec les informations présentes sur votre justificatif d’identité :{' '}
                    <span className={styles.universignErrorBold}>
                      {user?.nom}, {user?.prenom}, le {birthdate.split('-').reverse().join('/')}
                    </span>
                  </p>

                  <p className={styles.universignErrorBold}>
                    Nous vous invitons à re-télécharger un fichier de meilleure qualité ou un autre justificatif
                    d’identité.
                  </p>

                  <p className={styles.universignErrorText}>
                    Si le problème persiste ou que vos coordonnées ont changé, veuillez contacter notre Service Client
                    au{' '}
                    <span
                      className={styles.universignErrorPhone}
                      onClick={() => handlePhoneCall()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handlePhoneCall();
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label="Appeler le service client au 01 47 20 33 00"
                    >
                      01 47 20 33 00
                    </span>
                    .
                  </p>
                </div>
              </MtxGrid>
            </MtxGrid>
          )}

          {versementJustificatifError.coordonneesUtilisees.mail &&
            versementJustificatifError.coordonneesUtilisees.phone === false && (
              <MtxGrid row>
                <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                  <div className={styles.universignError}>
                    <h3>
                      <i className="totem-icon totem-icon-cross-circle" />
                      Erreur avec la signature électronique
                    </h3>

                    <p className={styles.universignErrorText}>
                      Notre prestataire de signature électronique nous informe que vous avez déjà réalisé une signature
                      avec un email différent : <span className={styles.universignErrorBold}>{emailTelUniversign}</span>
                    </p>

                    <p className={styles.universignErrorBold}>
                      Vous devez re-saisir cet email afin de valider votre signature électronique :
                    </p>

                    <form
                      onSubmit={(e) => {
                        handleClick(e);
                      }}
                    >
                      <TextInput
                        label="Email"
                        labelFor="universign-error-email"
                        placeholder="Adresse email"
                        type="email"
                        isError={universignTelMailError}
                        textError={universignTelMailErrorText}
                        onChange={(e) => {
                          setUniversignCoordonnees({ ...universignCoordonnees, email: e.currentTarget.value });
                          setUniversignTelMailError(false);
                        }}
                      />
                      <input className="hide-element" type="submit" />
                    </form>

                    <p className={styles.universignErrorText}>
                      Si le problème persiste ou que vos coordonnées ont changé, veuillez contacter notre Service Client
                      au{' '}
                      <span
                        className={styles.universignErrorPhone}
                        onClick={() => handlePhoneCall()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePhoneCall();
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label="Appeler le service client au 01 47 20 33 00"
                      >
                        01 47 20 33 00
                      </span>
                      .
                    </p>
                  </div>
                </MtxGrid>
              </MtxGrid>
            )}

          {versementJustificatifError.coordonneesUtilisees.mail === false &&
            versementJustificatifError.coordonneesUtilisees.phone && (
              <MtxGrid row>
                <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                  <div className={styles.universignError}>
                    <h3>
                      <i className="totem-icon totem-icon-cross-circle" />
                      Erreur avec la signature électronique
                    </h3>

                    <p className={styles.universignErrorText}>
                      Notre prestataire de signature électronique nous informe que vous avez déjà réalisé une signature
                      avec un numéro de téléphone portable différent :{' '}
                      <span className={styles.universignErrorBold}>{emailTelUniversign}</span>
                    </p>

                    <p className={styles.universignErrorBold}>
                      Vous devez re-saisir ce numéro de téléphone portable et re-télécharger votre facture de téléphone
                      mobile afin de valider votre signature électronique :
                    </p>

                    <label className="text-input-top-label">
                      N° de téléphone
                      <IntlTelInput
                        initialCountry="fr"
                        separateDialCode={false}
                        showFlags={true}
                        strictMode={true}
                        formatAsYouType={true}
                        formatOnDisplay={true}
                        fixDropdownWidth={true}
                        nationalMode={false}
                        onChangeNumber={(value) => OnChangePhoneInputError(value)}
                        onChangeValidity={(isValid) => setIsPhoneValid(isValid)}
                        inputProps={{
                          className:
                            'rappel-tel-input-container ' +
                            (universignTelMailError && 'rappel-tel-input-container-error'),
                          name: 'phone',
                          placeholder: '06 00 00 00 00',
                          required: true,
                        }}
                      />
                      {universignTelMailError && <FormTextError text={universignTelMailErrorText} />}
                    </label>

                    <p className={styles.universignErrorText}>
                      Si le problème persiste ou que vos coordonnées ont changé, veuillez contacter notre Service Client
                      au{' '}
                      <span
                        className={styles.universignErrorPhone}
                        onClick={() => handlePhoneCall()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePhoneCall();
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label="Appeler le service client au 01 47 20 33 00"
                      >
                        01 47 20 33 00
                      </span>
                      .
                    </p>
                  </div>
                </MtxGrid>
              </MtxGrid>
            )}

          {versementJustificatifError.coordonneesUtilisees.mail &&
            versementJustificatifError.coordonneesUtilisees.phone && (
              <MtxGrid row>
                <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                  <div className={styles.universignError}>
                    <h3>
                      <i className="totem-icon totem-icon-cross-circle" />
                      Erreur avec la signature électronique
                    </h3>

                    <p className={styles.universignErrorText}>
                      Notre prestataire de signature électronique nous informe que vos coordonnées ont déjà été
                      utilisées par un précédent signataire.
                    </p>

                    <p className={styles.universignErrorBold}>
                      Nous vous invitons à contacter notre Service Client au{' '}
                      <span
                        className={styles.universignErrorPhone}
                        onClick={() => handlePhoneCall()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePhoneCall();
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label="Appeler le service client au 01 47 20 33 00"
                      >
                        01 47 20 33 00
                      </span>{' '}
                      afin qu’un conseiller vous indique la marche à suivre.
                    </p>
                  </div>
                </MtxGrid>
              </MtxGrid>
            )}

          {versementJustificatifError.pieceExpire && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <div className={styles.universignError}>
                  <h3>
                    <i className="totem-icon totem-icon-cross-circle" />
                    Erreur avec la pièce d&apos;identité
                  </h3>

                  <p className={styles.universignErrorText}>
                    La date de validation de votre justificatif d&apos;identité est expirée.
                  </p>

                  <p className={styles.universignErrorBold}>
                    Nous vous invitons à re-télécharger votre justificatif d&apos;identité, en veillant à ce que le
                    document soit bien en cours de validité.
                  </p>

                  <p className={styles.universignErrorText}>
                    Si le problème persiste ou que vos coordonnées ont changé, veuillez contacter notre Service Client
                    au{' '}
                    <span
                      className={styles.universignErrorPhone}
                      onClick={() => handlePhoneCall()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handlePhoneCall();
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label="Appeler le service client au 01 47 20 33 00"
                    >
                      01 47 20 33 00
                    </span>
                    .
                  </p>
                </div>
              </MtxGrid>
            </MtxGrid>
          )}

          {!(
            (versementJustificatifError.coordonneesUtilisees.mail &&
              versementJustificatifError.coordonneesUtilisees.phone) ||
            versementJustificatifError.coordonneesUtilisees.mail
          ) && <h3 className={styles.sectionTitle}>Saisie de vos documents</h3>}

          {((state?.data.validFile['justificatif-identite-1'] === undefined && !errorJustificatif) ||
            (errorJustificatif && versementJustificatifError.infosNonCorrespondante) ||
            (errorJustificatif && versementJustificatifError.pieceLu) ||
            (errorJustificatif && versementJustificatifError.pieceExpire)) && (
            <MtxGrid row>
              <MtxGrid
                className={styles.idLabelContainer}
                col={12}
                colXs={12}
                colSm={12}
                colMd={12}
                colLg={12}
                colXl={12}
              >
                <label className={styles.idLabel} htmlFor="choix-piece-identite">
                  Justificatif d&apos;identité
                </label>
              </MtxGrid>

              <MtxGrid className={styles.selectField} col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <select
                  id="choix-piece-identite"
                  className={styles.idSelect}
                  value={versementJustificatifsData.typeDocumentIdentite}
                  onChange={(e) => {
                    handleSelectChange(e);
                  }}
                  required
                >
                  {options.map((option) => (
                    <option className="" key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </MtxGrid>
            </MtxGrid>
          )}

          {((state?.data.validFile['justificatif-identite-1'] === undefined && !errorJustificatif) ||
            (errorJustificatif && versementJustificatifError.infosNonCorrespondante) ||
            (errorJustificatif && versementJustificatifError.pieceExpire)) && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <div className={styles.infoBoxPage}>
                  <i className="totem-icon totem-icon-info" />
                  <p>
                    <button className={styles.infoBoxUnderline} onClick={() => openBonnesPratiquesModale()}>
                      Accédez à nos conseils
                    </button>{' '}
                    pour faciliter la validation de votre justificatif d&apos;identité
                  </p>
                </div>
              </MtxGrid>
            </MtxGrid>
          )}

          <div className="scroll-anchor" id="faire-versement-justif-anchor-pi"></div>

          {pieceIdentiteInput}

          {((state?.data.validFile['justificatif-telephone'] === undefined && !errorJustificatif) ||
            (errorJustificatif &&
              versementJustificatifError.coordonneesUtilisees.phone === true &&
              versementJustificatifError.coordonneesUtilisees.mail === false)) && (
            <MtxGrid className={styles.documentInput} row>
              <div className="scroll-anchor" id="faire-versement-justif-anchor-telephone"></div>
              <MtxGrid className={styles.phoneContainer} col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <i
                  className="totem-icon totem-icon-info"
                  onClick={() => {
                    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                      event_id: 'clic',
                      event_categorie: 'clic',
                      event_metier: 'espace client placement',
                      event_nom: 'info facture telephone',
                      event_detail_1: 'picto',
                      event_detail_2: `${versementData.contratFamille} - ${versementData.contratNomProduit}`,
                    });
                    openModal('showModalTelephone');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                        event_id: 'clic',
                        event_categorie: 'clic',
                        event_metier: 'espace client placement',
                        event_nom: 'info facture telephone',
                        event_detail_1: 'picto',
                        event_detail_2: `${versementData.contratFamille} - ${versementData.contratNomProduit}`,
                      });
                      openModal('showModalTelephone');
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Informations sur la facture de téléphone mobile"
                />
                <InputFile
                  formats={formatsValides}
                  label="Facture de téléphone mobile"
                  maxSize={tailleMaxMo}
                  displayedMaxSize={4}
                  blobUrl={justificatifsUrl.justificatifTelephoneURL}
                  isError={erreurCompression['justificatif-telephone']}
                  errorMessageInvalid={
                    erreurCompression['justificatif-telephone'] ? 'La taille du fichier dépasse les 4 Mo autorisés' : ''
                  }
                  whichFile="justificatif-telephone"
                  disableInput={versementJustificatifsData.justificatifTelephone !== ''}
                  setFile={setFile}
                  handleFileValidity={handleFileValidity}
                />

                {missingValue && versementJustificatifsData.justificatifTelephone === '' && (
                  <FormTextError text="Champs requis" />
                )}
              </MtxGrid>
            </MtxGrid>
          )}

          {(state?.data.validFile['justificatif-origine_fonds'] === false ||
            state?.data.validFile['justificatif-origine_fonds'] === true) &&
            !errorJustificatif &&
            showOrigineFondsInput && (
              <div className={styles.fundFileInputs}>
                <div>
                  <div className="scroll-anchor" id="faire-versement-justif-anchor-origine-fonds"></div>
                  <h3>Justificatif d&apos;origine de fonds</h3>

                  <MtxGrid row>
                    <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                      <InputFile
                        formats={formatsValides}
                        label={origineFondsLabel}
                        maxSize={tailleMaxMo}
                        displayedMaxSize={4}
                        blobUrl={justificatifsUrl.justificatifOrigineFondsURL}
                        isError={erreurCompression['justificatif-origine_fonds']}
                        errorMessageInvalid={
                          erreurCompression['justificatif-origine_fonds']
                            ? 'La taille du fichier dépasse les 4 Mo autorisés'
                            : ''
                        }
                        whichFile="justificatif-origine_fonds"
                        disableInput={versementJustificatifsData.justificatifOrigineFonds !== ''}
                        setFile={setFile}
                        handleFileValidity={handleFileValidity}
                      />

                      {missingValue && versementJustificatifsData.justificatifOrigineFonds === '' && (
                        <FormTextError text="Champs requis" />
                      )}
                    </MtxGrid>
                  </MtxGrid>

                  <MtxGrid row>
                    <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                      <div className={`${styles.infoBox} ${styles.infoBoxLast}`}>
                        <i className="totem-icon totem-icon-info" />
                        <p>{origineFondsInfo}</p>
                      </div>
                    </MtxGrid>
                  </MtxGrid>
                </div>
              </div>
            )}

          {!(
            (versementJustificatifError.coordonneesUtilisees.mail &&
              versementJustificatifError.coordonneesUtilisees.phone) ||
            versementJustificatifError.coordonneesUtilisees.mail
          ) && (
            <MtxGrid row>
              <MtxGrid col={6} colXs={12} colSm={9} colMd={9} colLg={6} colXl={6}>
                <span className={styles.mandatoryText}>Mentions obligatoires</span>
              </MtxGrid>
            </MtxGrid>
          )}

          <MtxGrid row>
            <MtxGrid className={styles.confirmButton} col={4} colXs={12} colSm={6} colMd={6} colLg={4} colXl={4}>
              {showContactConseillerButton ? (
                <>
                  <MtxButton
                    className="tchatclose1"
                    onClick={() => openModalRappel()}
                    variant="primary"
                    size="medium"
                    fullWidth={true}
                  >
                    <div>Contacter un conseiller</div>
                  </MtxButton>

                  <MtxButton
                    className="tchatopen1"
                    style={{ display: 'none' }}
                    variant="primary"
                    size="medium"
                    fullWidth={true}
                    onClick={() => {}}
                  >
                    <div>Contacter un conseiller</div>
                  </MtxButton>
                </>
              ) : (
                <MtxButton
                  onClick={handleClick}
                  variant="primary"
                  size="large"
                  fullWidth={true}
                  disabled={disableSubmitBtn}
                >
                  <div>{submitButtonLabel}</div>
                </MtxButton>
              )}
            </MtxGrid>
          </MtxGrid>
        </div>
      </MtxGrid>
    </>
  );
};
